import React, { useEffect, useMemo, useState } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { useNavigate, useParams } from 'react-router-dom';
import config from './config';
import { getOrganizationContext, getOrganizationHeaders } from './orgContext';
import { AdminBreadcrumbs } from './admin/components/AdminUiPrimitives';
import { AdminPageHeader } from './admin/components/AdminMasterDetail';

interface AssignmentItem {
  id: number;
  examId?: number | null;
  examName?: string | null;
  type: string;
  title: string;
  description?: string | null;
  status: string;
  dueAt?: string | null;
  questionCount: number;
  maxAttempts: number;
  allowLateSubmission: boolean;
}

const AssignmentsHub: React.FC = () => {
  const { isAuthenticated, isLoading, loginWithRedirect, getAccessTokenSilently } = useAuth0();
  const navigate = useNavigate();
  const { organizationSlug } = useParams<{ organizationSlug?: string }>();

  const [assignments, setAssignments] = useState<AssignmentItem[]>([]);
  const [selectedAssignmentId, setSelectedAssignmentId] = useState<number | null>(null);
  const [resolvedOrganizationId, setResolvedOrganizationId] = useState<number | null>(null);
  const [resolvingOrganization, setResolvingOrganization] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const withOrgPath = (path: string) => {
    if (organizationSlug) {
      return `/${organizationSlug}${path}`;
    }

    return path;
  };

  useEffect(() => {
    if (!isAuthenticated) {
      setResolvedOrganizationId(null);
      return;
    }

    const resolveOrganization = async () => {
      if (!organizationSlug) {
        setResolvedOrganizationId(getOrganizationContext()?.organizationId ?? null);
        return;
      }

      setResolvingOrganization(true);
      try {
        const token = await getAccessTokenSilently();
        const response = await fetch(`${config.apiBaseUrl}/api/organizations/my`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        if (!response.ok) {
          setResolvedOrganizationId(null);
          setError('Unable to resolve organization context for this route.');
          return;
        }

        const organizations = await response.json();
        const matched = Array.isArray(organizations)
          ? organizations.find((item: any) => String(item?.organizationSlug || '').toLowerCase() === organizationSlug.toLowerCase())
          : null;

        setResolvedOrganizationId(matched?.organizationId ? Number(matched.organizationId) : null);
        if (!matched?.organizationId) {
          setError('You do not have access to this organization context.');
        }
      } catch {
        setResolvedOrganizationId(null);
        setError('Unable to resolve organization context for this route.');
      } finally {
        setResolvingOrganization(false);
      }
    };

    void resolveOrganization();
  }, [getAccessTokenSilently, isAuthenticated, organizationSlug]);

  useEffect(() => {
    if (!isAuthenticated) {
      return;
    }

    if (organizationSlug && (resolvingOrganization || !resolvedOrganizationId)) {
      return;
    }

    const loadAssignments = async () => {
      try {
        setLoading(true);
        setError(null);

        const token = await getAccessTokenSilently();
        const orgHeaders = getOrganizationHeaders();
        const headers: Record<string, string> = {
          Authorization: `Bearer ${token}`,
          ...orgHeaders,
        };

        if (resolvedOrganizationId) {
          headers['X-Organization-Id'] = String(resolvedOrganizationId);
        }

        const response = await fetch(`${config.apiBaseUrl}/api/assignments/my`, {
          headers,
        });

        if (!response.ok) {
          const payload = await response.json().catch(() => ({}));
          throw new Error(payload?.message || 'Failed to load assignments.');
        }

        const data = await response.json();
        const rows = Array.isArray(data) ? data : [];
        setAssignments(rows);
        if (rows.length > 0) {
          setSelectedAssignmentId((current) => current ?? rows[0].id);
        }
      } catch (err: any) {
        setError(err?.message || 'Failed to load assignments.');
      } finally {
        setLoading(false);
      }
    };

    void loadAssignments();
  }, [getAccessTokenSilently, isAuthenticated, organizationSlug, resolvedOrganizationId, resolvingOrganization]);

  const selectedAssignment = useMemo(
    () => assignments.find((item) => item.id === selectedAssignmentId) ?? null,
    [assignments, selectedAssignmentId]
  );

  const openAssignment = (assignment: AssignmentItem) => {
    const normalizedType = (assignment.type || '').trim().toLowerCase();

    if (normalizedType === 'exam' && assignment.examId) {
      navigate(withOrgPath(`/exam-center/${assignment.examId}`));
      return;
    }

    navigate(withOrgPath(`/assignments/${assignment.id}/homework`));
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-16">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-500 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      <div className="text-center py-12">
        <h2 className="text-xl font-bold text-slate-800 mb-2">Assignments</h2>
        <p className="text-slate-600">Please sign in to view your assignments.</p>
      </div>
    );
  }

  return (
    <div style={{ display: 'grid', gap: 16 }}>
      <AdminBreadcrumbs items={[
        { label: 'Dashboard', to: '/analytics' },
        { label: 'Assignments', isCurrent: true },
      ]} />
      <AdminPageHeader
        title="Assignments"
        subtitle="Your homework and exam assignments"
        actions={[]}
      />

      {error && (
        <div className="rounded-xl border border-red-300 bg-red-50 px-3 py-3 text-sm text-red-700">
          {error}
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-3 sm:gap-4">
          <div className="lg:col-span-4 lg:h-full lg:min-h-0">
            <div className="bg-white rounded-lg shadow-md overflow-hidden flex flex-col lg:h-full lg:min-h-0">
              <div className="px-3 py-2.5 border-b border-gray-100 bg-gray-50">
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 rounded-lg bg-indigo-100 flex items-center justify-center text-lg shrink-0">
                    📚
                  </div>
                  <div className="min-w-0 text-left">
                    <p className="text-sm font-bold text-gray-900 leading-tight">Assignments</p>
                  </div>
                </div>
              </div>

              <div className="flex-1 min-h-0 p-2 md:p-2.5 space-y-2 overflow-y-auto">
                {loading ? (
                  <div className="min-h-[200px] flex items-center justify-center text-sm text-indigo-500">Loading assignments...</div>
                ) : assignments.length === 0 ? (
                  <div className="rounded-md border border-dashed border-gray-200 min-h-[280px] flex flex-col items-center justify-center gap-2 text-sm text-gray-500">
                    <span className="text-3xl">&#128218;</span>
                    <span className="font-medium">No assignments found.</span>
                  </div>
                ) : (
                  assignments.map((assignment) => {
                    const isActive = selectedAssignmentId === assignment.id;
                    return (
                      <button
                        key={assignment.id}
                        type="button"
                        onClick={() => setSelectedAssignmentId(assignment.id)}
                        className={`w-full rounded-lg border px-3 py-2.5 text-left transition-all ${
                          isActive
                            ? 'border-indigo-300 bg-indigo-50 shadow-sm'
                            : 'border-gray-200 bg-white hover:border-gray-300 hover:bg-gray-50'
                        }`}
                      >
                        <p className={`text-sm font-semibold truncate ${isActive ? 'text-indigo-900' : 'text-slate-800'}`}>
                          {assignment.title}
                        </p>
                        <p className="mt-1 text-xs text-slate-500">
                          {assignment.type} • {assignment.questionCount} questions
                        </p>
                      </button>
                    );
                  })
                )}
              </div>
            </div>
          </div>

          <div className="lg:col-span-8 lg:h-full lg:min-h-0">
            <div className="bg-white rounded-lg shadow-md overflow-hidden flex flex-col lg:h-full lg:min-h-0">
              {!selectedAssignment ? (
                <div className="min-h-[280px] p-8 flex items-center justify-center">
                  <div className="rounded-2xl border border-dashed border-slate-300 bg-white/70 py-10 px-6 text-center max-w-lg">
                    <div className="mb-3 text-4xl">🧭</div>
                    <p className="text-base font-semibold text-slate-700 sm:text-lg">Select an assignment to view details.</p>
                  </div>
                </div>
              ) : (
                <div className="flex-1 min-h-0 p-3 sm:p-4 md:p-5 lg:overflow-y-auto">
                  <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-lg">
                    <div className="bg-gradient-to-r from-app-header-from to-app-header-to px-4 py-3 text-white">
                      <div className="flex flex-wrap items-center gap-2 text-left">
                        <span className="text-base font-bold sm:text-lg leading-tight truncate mr-2">{selectedAssignment.title}</span>
                        <span className="inline-flex items-center rounded-full bg-white/20 px-2 py-0.5 text-xs font-semibold">{selectedAssignment.type}</span>
                      </div>
                      <p className="mt-1 text-xs text-app-header-muted leading-snug">{selectedAssignment.description || 'No description available'}</p>
                    </div>

                    <div className="p-4 sm:p-5">
                      <div className="mb-5 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
                        <div className="rounded-xl border border-blue-100 bg-blue-50 px-4 py-3">
                          <p className="text-xs font-semibold uppercase tracking-wide text-blue-700">Questions</p>
                          <p className="mt-1 text-lg font-bold text-slate-800">{selectedAssignment.questionCount}</p>
                        </div>
                        <div className="rounded-xl border border-indigo-100 bg-indigo-50 px-4 py-3">
                          <p className="text-xs font-semibold uppercase tracking-wide text-indigo-700">Type</p>
                          <p className="mt-1 text-lg font-bold text-slate-800">{selectedAssignment.type}</p>
                        </div>
                        <div className="rounded-xl border border-emerald-100 bg-emerald-50 px-4 py-3">
                          <p className="text-xs font-semibold uppercase tracking-wide text-emerald-700">Status</p>
                          <p className="mt-1 text-lg font-bold text-slate-800">{selectedAssignment.status}</p>
                        </div>
                        <div className="rounded-xl border border-amber-100 bg-amber-50 px-4 py-3">
                          <p className="text-xs font-semibold uppercase tracking-wide text-amber-700">Due</p>
                          <p className="mt-1 text-sm font-bold text-slate-800">
                            {selectedAssignment.dueAt ? new Date(selectedAssignment.dueAt).toLocaleString() : 'No due date'}
                          </p>
                        </div>
                      </div>

                      <button
                        type="button"
                        onClick={() => openAssignment(selectedAssignment)}
                        className="rounded-lg bg-indigo-600 px-4 py-2 font-semibold text-white transition-colors hover:bg-indigo-700"
                      >
                        {selectedAssignment.type.toLowerCase() === 'exam' ? 'Open Exam' : 'Open Homework'}
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
    </div>
  );
};

export default AssignmentsHub;
